// Build-time-imported image model facts for the /models/:slug pages.
//
// The canonical source is website/content/image-models.json (git-tracked, committed
// content). It lives INSIDE the website package for the same reason whats-new.json
// does: the site builds in Docker with `context: ./website`, so a build-time import
// must resolve within website/. AI Studio's model detail imports THIS module (the
// client's @website alias), so the picker and the pages share the facts, the wording
// and the formatting. A .NET test pins the file to the server catalog.

// Relative, not the @content alias: vite.config.ts imports this module before the alias exists.
import raw from '../../content/image-models.json'
import { TIER_ORDER, relatedByTier, type ModelTier } from './modelTiers'

export type ImageModelTier = ModelTier
export type ImageModelInputs = 'text' | 'photos' | 'style'

export interface ImageModelQuality {
  /** The server's quality key (matches the pricing feed's `quality`). */
  quality: string
  label?: string
  tier: ImageModelTier
  /** [min, max] seconds measured for one picture. */
  seconds: [number, number]
}

export interface ImageModelSample {
  src: string
  alt: string
  prompt?: string
}

export interface ImageModel {
  modelId: string
  slug: string
  name: string
  goodFor: string
  summary: string
  strengths: string[]
  limits: string[]
  inputs: ImageModelInputs
  qualities: ImageModelQuality[]
  samples: ImageModelSample[]
}

/** What a model takes, in the words both the site and AI Studio show. */
export const INPUTS_TEXT: Record<ImageModelInputs, string> = {
  text: 'Your description only',
  photos: 'Your description, and photos if you add them',
  style: "Your description, and style pictures that set the look (they aren't edited)",
}

/** "12–37", or the low end alone when the measured range is too narrow to be worth a range. */
export function secondsText(q: ImageModelQuality): string {
  const [lo, hi] = q.seconds
  return hi - lo > 3 ? `${lo}–${hi}` : `${lo}`
}

export { TIER_ORDER }

export const imageModels: ImageModel[] = (raw as unknown as { models: ImageModel[] }).models

export function imageModelBySlug(slug: string): ImageModel | undefined {
  return imageModels.find((m) => m.slug === slug)
}

/** Other image models sharing a tier with this one, ordered by tier then file order. */
export function otherModelsByTier(model: ImageModel): { tier: ImageModelTier; models: ImageModel[] }[] {
  return relatedByTier(model, imageModels)
}
